import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Col } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import AddProfileForm from './AddProfileForm';
import AddPaymentDetailsForm from './AddPaymentDetailsForm';
import RegisterSuccess from '../RegisterSuccess';
import RouterPaths from '../../constants/RouterPaths';
import logo from '../../assets/img/logonew.svg';
import logoMobile from '../../assets/img/icons/logo-mobile.svg';
import arrowBack from '../../assets/img/icons/arrow_back.svg';
import { useMediaQuery } from '../../common/hooks/useMediaQuery';
import CompanyDetailsForm from './CompanyDetailsForm';
import Progress from './progress';
import LoginPromptBanner from './LoginPromptBanner';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';

function RegisterForm({ values, setFieldValue, step, setStep }) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const logoSrc = useMemo(() => (isMobile ? logoMobile : logo), [isMobile]);

  const prevStep = useCallback(() => {
    setStep((prevState) => prevState - 1);
  }, [setStep]);

  const nextStep = useCallback(() => {
    setStep((prevState) => prevState + 1);
  }, [setStep]);

  const handleBackClick = useCallback(() => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    } else {
      navigate(RouterPaths.HOMEPAGE);
    }
  }, [step, navigate, setStep]);

  const renderStep = useCallback(() => {
    switch (step) {
      case 1:
        return (
          <CompanyDetailsForm
            prevStep={prevStep}
            setFieldValue={setFieldValue}
            values={values}
            nextStep={nextStep}
          />
        );
      case 2:
        return <AddProfileForm prevStep={prevStep} nextStep={nextStep} values={values} />;
      case 3:
        return (
          <AddPaymentDetailsForm prevStep={prevStep} nextStep={nextStep} values={values} />
        );
      case 4:
        return <RegisterSuccess />;
      default:
        return null;
    }
  }, [step, prevStep, nextStep, setFieldValue, values]);

  const headerContent = useMemo(
    () => (
      <div className="register-page__steps-header">
        <div className="register-page__steps-header-logo">
          <Link to={RouterPaths.HOMEPAGE}>
            <img src={logoSrc} alt="logo" />
          </Link>
          <CommonButton
            className="register__back-button"
            type="button"
            label="Back"
            variant={CommonButtonVariants.OUTLINED}
            icon={arrowBack}
            iconAlt="Back to homepage"
            iconLeft
            handleClick={(e) => {
              handleBackClick();
              e.currentTarget.blur();
            }}
          />
        </div>

        <h2>Register today in just 3 easy steps</h2>
        <p>
          <b>Access a consistent supply of franchised dealer stock.</b>
          <br /> If you&#39;re a franchised dealer, you&#39;ll also be approved to list your
          own vehicles. No subscription costs. No hidden fees. Register today and unlock the
          stock.
        </p>
      </div>
    ),
    [handleBackClick, logoSrc],
  );

  return (
    <div className="register-page m-0 p-0">
      {step <= 3 ? (
        <>
          <Col className="register-page__steps-column" xxl={8} lg={12}>
            {headerContent}
            <div className="form-container">
              <Progress step={step} />
              {renderStep()}
            </div>
          </Col>
          <Col className="register-page__banner-column" xxl={4} lg={12}>
            <LoginPromptBanner />
          </Col>
        </>
      ) : (
        <Col md={12} className="register-success-container">
          {renderStep()}
        </Col>
      )}
    </div>
  );
}

RegisterForm.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  step: PropTypes.number.isRequired,
  setStep: PropTypes.func.isRequired,
};

export default RegisterForm;
